import React, { ReactElement } from 'react';
import axios from 'app/infrastructure/axiosInstance';
import { FormikProps, FormikValues } from 'formik';
import { FormBuilderTypes, FormField, FormFields } from 'app/modules/tp/builders/FormBuilder';
import { Entity } from 'app/models/entity';
import _, { isArray, parseInt } from 'lodash';
import { Accordion } from 'react-bootstrap';
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';
import rideApi from "app/modules/tp/rides/_requests";
import { Actions } from 'app/modules/tp/components/Modal/ModalEntity';

export type FieldsBuilder = (e: any) => React.ReactElement<any, any> | null

export type MultiplyFieldProps<T, X> = {
    formik: FormikProps<T>,
    inputObj: FormField<X>
    initialCount?: number,
    isSubForm?: boolean,
    fieldBuilder: FieldsBuilder
    actions: Actions<T>,
    entity: T
}

export function getParentKey(key: string) {
    const splitKey = key.split('.');
    const length = splitKey.length;
    if (length > 1) {
        return splitKey[length - 2];
    }
    return key;
}

function upsert(array: Array<any>, element: any) {
    const i = array.findIndex(_element => _element.i === element.i);
    (i > -1) ? array[i] = element : array.push(element);
    return [...array];
}

const changeAllSubFormNames = (fields: JSX.Element[], count: number) =>
    fields.map((field) => {
        let keyTree = (field.props.name as string)?.split('.')
        let name = (keyTree.shift()?.concat(`[${count}]`))?.concat(keyTree.join('.'));
        field.props.key = field.props.name = name;
        return field;
    })

export const appendMultipleParentSubFormKeys = <X extends Entity,>(parentKey: string, formFields: FormFields<X>, counter: number = 0) => {
    Object.keys(formFields).map((key: keyof FormFields<X>) => {
        if ((formFields[key]?.key?.split(".")[0]?.replace(/\[.*\]/, "")) === parentKey) {
            return formFields;
        }
        return formFields[key].key = `${parentKey}[${counter}].${formFields[key]?.key}`;
    })
    return formFields;
}

export const appendCounterArrayToKey = <X extends Entity,>(formField: FormField<X>, counter: number) => {
    formField.key = `${formField?.key}[${counter}]`;
    return formField;
}

export const appendParentToKey = <X extends Entity,>(parentKey: string, formField: FormField<X>) => {
    if ((formField?.key?.split(".")[0]?.replace(/\[.*\]/, "")) === parentKey) {
        return formField;
    }
    formField.key = `${parentKey}.${formField?.key}`;
    return formField;
}

export const appendParentToKeys = <X extends Entity,>(parentKey: string, formField: FormFields<X>) => {
    Object.keys(formField).map((key: keyof FormFields<X>) => {
        if ((formField[key]?.key?.split(".")[0]?.replace(/\[.*\]/, "")) === parentKey) {
            return formField;
        }
        formField[key].key = `${parentKey}.${formField[key]?.key}`;
        return formField;
    })
    return formField;
}

export const replaceBlueprintKey = <X extends Entity,>(formBlueprint: FormFields<X> | FormField<X>, parentKey: any, index: number) => {
    let newFormBlueprint = _.cloneDeep(formBlueprint);
    newFormBlueprint.multiple = false;
    const keys = newFormBlueprint?.key?.split(".");
    if (keys?.length > 1 && keys[0]?.replace(/\[.*\]/, "") === parentKey) {
        return formBlueprint;
    }

    if ((formBlueprint as FormFields<X>)?.form) {
        newFormBlueprint = appendMultipleParentSubFormKeys(parentKey, (newFormBlueprint as FormBuilderTypes<X>).form, index);
    } else {
        newFormBlueprint = appendCounterArrayToKey(newFormBlueprint as FormField<X>, index);
    }

    return newFormBlueprint;
}

const createSubEntity = <T, X extends Entity,>(formik: FormikProps<T>, parentKey: keyof T, fieldBlueprint: FormField<X>): X => {
    return Object.keys(fieldBlueprint ?? {}).reduce((acc: any, key: string) => {
        acc[key] = fieldBlueprint[key as keyof FormField<X>]?.initialValue ?? null;
        return acc;
    }, {});
}

const createRenderedFieldArray = <X extends Entity,>(fieldBuilder: FieldsBuilder, fieldBlueprint: FormField<X>, times: number, parentEntityKey: string) => {
    const renderedFields = [];
    for (let i = 0; i < times; i++) {
        let form: (FormFields<X> | FormField<X>) = fieldBlueprint;
        renderedFields.push(fieldBuilder(replaceBlueprintKey(form, parentEntityKey, i)));
    }
    return renderedFields;
}

const renderFields = <T, X extends Entity,>(fieldBlueprint: FormField<X>, fieldValues: FormikValues, fieldBuilder: FieldsBuilder, entity: T, isSubForm: boolean = false, counter: number) => {
    if (fieldValues.length === undefined) {
        return null;
    }
    const parentEntityKey = isSubForm ? fieldBlueprint?.key : null;
    return createRenderedFieldArray<X>(fieldBuilder, fieldBlueprint, counter, parentEntityKey);
}

export default function MultiplyField<T extends Entity, X extends Entity>({ formik, inputObj, fieldBuilder, initialCount = 0, isSubForm = false, entity, actions }: MultiplyFieldProps<T, X>) {


    const [selectedAccordion, setSelectedAccordion] = React.useState<AccordionEventKey>(
        Array(renderFields?.length ?? 0).fill(0).map((_, i) => i.toString())
    );
    const componentKey = React.useMemo(() => inputObj?.key as string, [inputObj.key]);

    const fieldBlueprint = React.useMemo(() =>
        isSubForm ? (inputObj as FormBuilderTypes<X>).form : inputObj,
        [inputObj]);

    const fieldValues = React.useMemo(() => formik.values[componentKey] || [], [formik.values, componentKey]);

    const removeLabel = React.useMemo(() => inputObj?.removeLabel || <i className="fa-solid fa-trash-can"></i>, [inputObj.removeLabel]);
    const additionalLabel = React.useMemo(() => inputObj?.duplicateLabel || <i className="fa-solid fa-copy"></i>, [inputObj.duplicateLabel]);

    const [count, setCount] = React.useState<number>(entity[componentKey]?.length || initialCount);

    //const [fields, setFields] = React.useState<JSX.Element[]>([...Array(initialCount)].map((_, i) => (
    //    React.cloneElement(creationFunction() as ReactElement<any,string>, { onChange: (e: any) => onChange(e, i) })
    //)));
    //const [state, setState] = React.useState<Array<any>>(loadState(formik, inputObj));


    //const onChange = (e: any, i: number) => {
    //    setState(upsert(state, { i, e }));
    //}

    const add = () => {
        if (isSubForm) {
            formik.setFieldValue(componentKey, [...(formik.values[componentKey] ?? []), createSubEntity<T, X>(formik, componentKey, fieldBlueprint as FormField<X>)]);
        }
        setCount(count + 1);
    }

    const remove = React.useCallback((i: number) => {
        formik.setValues({ ...formik.values, [componentKey]: formik.values[componentKey].filter((_: any, index: number) => index !== i) });
        setCount(count - 1);
    }, [formik?.values]);

    const duplicate = React.useCallback((i: number) => {
        //HACK: dynamic entity duplication instead of direct resource call
        if (componentKey === "rides" && window.confirm("Attenzione! Assicurati di aver salvato prima di duplicare un elemento. Vuoi procedere?")) {
            rideApi
                .duplicate(formik.values[componentKey][i].id)
                .then((response) => {
                    actions.reload.onClick(entity);
                })
            return;
        }
    }, [formik?.values]);

    const isAccordionSelected = (key: number | string) => (
        Array.isArray(selectedAccordion) ? selectedAccordion.includes(key?.toString()) : selectedAccordion === key?.toString()
    )

    //const remove = React.useCallback((i: number) => {
    //    setFields(fields.filter((_, index) => index !== i));
    //    setState(state.filter((_, index) => index !== i));
    //}, [fields, state]);
    //
    //const renderFields = React.useCallback(() => {
    //    return fields.map((field, i) => (
    //        <div key={i}>
    //            {field}
    //            <button onClick={() => remove(i)} className="tp-btn-link-danger">
    //                {removeLabel || 'Elimina'}
    //            </button>
    //        </div>
    //    ));
    //}, [fields, remove, removeLabel]);
    //
    //React.useEffect(() => {
    //    formik.setFieldValue(inputObj.key as string, state.map(({ e }) => e));
    //}, [state]);
    //
    //const renderedFields = React.useMemo(() => renderFields(), [renderFields]);

    const renderedFields = React.useMemo(() =>
        renderFields(inputObj, fieldValues, fieldBuilder, entity, isSubForm, count)
        , [formik.values, fieldValues, count]);

    return (
        <Accordion className="tp-accordion" onSelect={(n: AccordionEventKey) => setSelectedAccordion(n)}
            alwaysOpen={true}
            defaultActiveKey={selectedAccordion}
        >
            {
                renderedFields?.map((field, i) => (
                    <Accordion.Item eventKey={i.toString()} key={i}>
                        <Accordion.Header>
                            <div className="w-100 tp-accordion-header d-flex justify-content-between align-items-center pe-3">
                                <div className="tp-accordion-title">
                                    <span className="pe-3">
                                        {isAccordionSelected(i) ? <i className="fa-solid fa-chevron-down"></i> : <i className="fa-solid fa-chevron-up"></i>}
                                    </span>
                                    {isAccordionSelected(i) && <i className="fa-solid fa-circle me-3"></i>}
                                    {inputObj?.label || 'Elemento'} {i + 1}
                                </div>
                                <div className='tp-accordion-remove'>
                                    {(formik.values[componentKey]?.length > i &&
                                        formik.values[componentKey][i]?.id &&
                                        <button type="button" onClick={() => duplicate(i)} className=" p-2 tp-btn-link-danger tp-accordion-remove me-3">
                                            {additionalLabel}
                                        </button> )|| null
                                    }
                                    <button type="button" onClick={() => remove(i)} className="p-2 tp-btn-link-danger tp-accordion-remove">
                                        {removeLabel}
                                    </button>
                                </div>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            {field}
                        </Accordion.Body>
                    </Accordion.Item>
                ))
            }
            <button type="button" onClick={() => add()} className="btn tp-btn tp-btn-primary mt-5 ">
                {inputObj?.addLabel || 'Aggiungi'}
            </button>
        </Accordion>
    )
}
